import { css, html, nothing } from 'lit';
import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';

class StaffSettingsView extends BaseComponent {
    static properties = {
        _sources: { type: Array, state: true },
        _loading: { type: Boolean, state: true },
        _error: { type: String, state: true },
        _saving: { type: Boolean, state: true },
        _newSlug: { type: String, state: true },
        _newLabel: { type: String, state: true },
        _webhookUrl: { type: String, state: true },
        _webhookSaving: { type: Boolean, state: true },
        _showcaseIds: { type: Array, state: true },
        _showcaseAssets: { type: Array, state: true },
        _allAssets: { type: Array, state: true },
        _showcaseSaving: { type: Boolean, state: true },
        _showcaseSearch: { type: String, state: true },
    };

    static styles = css`
        :host { display: block; }
        .page-header h4 { margin: 0; font-size: 1.5rem; font-weight: 600; margin-bottom: 1.5rem; }
        .section { border: 1px solid #eee; border-radius: 8px; padding: 1.25rem; margin-bottom: 1.25rem; }
        .section h6 { font-weight: 600; margin-bottom: 1rem; }
        table { font-size: 0.9rem; }
    `;

    constructor() {
        super();
        this._sources = [];
        this._loading = true;
        this._error = null;
        this._saving = false;
        this._newSlug = '';
        this._newLabel = '';
        this._webhookUrl = '';
        this._webhookSaving = false;
        this._showcaseIds = [];
        this._showcaseAssets = [];
        this._allAssets = [];
        this._showcaseSaving = false;
        this._showcaseSearch = '';
    }

    async connectedCallback() {
        super.connectedCallback();
        this._loadSources();
        this._loadWebhook();
        this._loadShowcase();
    }

    async _loadSources() {
        this._loading = true;
        this._error = null;
        try {
            const data = await api.get('/api/sources');
            this._sources = data.sources || [];
        } catch {
            this._sources = [];
        } finally {
            this._loading = false;
        }
    }

    // Run one change to the source list (issue #218): the API checks it — d3
    // and a pinned source can't be removed — and answers the whole list.
    async _changeSources(request) {
        this._saving = true;
        this._error = null;
        try {
            const data = await request();
            this._sources = data.sources || [];
        } catch (err) {
            this._error = err.message;
        } finally {
            this._saving = false;
        }
    }

    async _addSource(e) {
        e.preventDefault();
        if (!this._newSlug || !this._newLabel) return;
        const body = { slug: this._newSlug, label: this._newLabel };
        await this._changeSources(() => api.post('/api/sources', body));
        if (!this._error) {
            this._newSlug = '';
            this._newLabel = '';
        }
    }

    async _togglePublic(source) {
        await this._changeSources(() =>
            api.put(`/api/sources/${encodeURIComponent(source.slug)}`, {
                isPublic: !source.isPublic,
            }),
        );
    }

    async _setDefault(slug) {
        await this._changeSources(() =>
            api.put(`/api/sources/${encodeURIComponent(slug)}`, { isDefault: true }),
        );
    }

    async _loadWebhook() {
        try {
            const data = await api.get('/api/settings/discord_webhook_url');
            this._webhookUrl = data.value || '';
        } catch {
            this._webhookUrl = '';
        }
    }

    async _saveWebhook() {
        this._webhookSaving = true;
        this._error = null;
        try {
            await api.put('/api/settings/discord_webhook_url', { value: this._webhookUrl });
        } catch (err) {
            this._error = err.message;
        } finally {
            this._webhookSaving = false;
        }
    }

    async _removeSource(slug) {
        await this._changeSources(() => api.delete(`/api/sources/${encodeURIComponent(slug)}`));
    }

    async _loadShowcase() {
        try {
            const [showcaseData, assetsData] = await Promise.all([
                api.get('/api/settings/showcase_assets'),
                api.get('/api/assets?pageSize=500&source=d3'),
            ]);
            this._showcaseIds = showcaseData.value || [];
            this._allAssets = assetsData.assets || [];
            this._showcaseAssets = this._showcaseIds
                .map((id) => this._allAssets.find((a) => a.id === id))
                .filter(Boolean);
        } catch {
            this._showcaseIds = [];
            this._allAssets = [];
            this._showcaseAssets = [];
        }
    }

    async _saveShowcase() {
        this._showcaseSaving = true;
        this._error = null;
        try {
            await api.put('/api/settings/showcase_assets', { value: this._showcaseIds });
        } catch (err) {
            this._error = err.message;
        } finally {
            this._showcaseSaving = false;
        }
    }

    async _addToShowcase(asset) {
        if (this._showcaseIds.includes(asset.id)) return;
        this._showcaseIds = [...this._showcaseIds, asset.id];
        this._showcaseAssets = [...this._showcaseAssets, asset];
        this._showcaseSearch = '';
        await this._saveShowcase();
    }

    async _removeFromShowcase(id) {
        this._showcaseIds = this._showcaseIds.filter((i) => i !== id);
        this._showcaseAssets = this._showcaseAssets.filter((a) => a.id !== id);
        await this._saveShowcase();
    }

    async _moveShowcase(index, direction) {
        const newIds = [...this._showcaseIds];
        const newAssets = [...this._showcaseAssets];
        const swapIdx = index + direction;
        if (swapIdx < 0 || swapIdx >= newIds.length) return;
        [newIds[index], newIds[swapIdx]] = [newIds[swapIdx], newIds[index]];
        [newAssets[index], newAssets[swapIdx]] = [newAssets[swapIdx], newAssets[index]];
        this._showcaseIds = newIds;
        this._showcaseAssets = newAssets;
        await this._saveShowcase();
    }

    get _filteredAddableAssets() {
        if (!this._showcaseSearch) return [];
        const q = this._showcaseSearch.toLowerCase();
        return this._allAssets
            .filter((a) => !this._showcaseIds.includes(a.id))
            .filter((a) => a.slug.includes(q) || a.asset_type.includes(q))
            .slice(0, 10);
    }

    render() {
        return html`
            <div class="page-header">
                <h4>Settings</h4>
            </div>

            ${this._error ? html`<div class="alert alert-danger">${this._error}</div>` : ''}

            <div class="section">
                <h6>Asset Sources</h6>
                <p class="text-muted small">The sources art is added under: where it came from. Removing one only takes it off the pickers; its versions stay. The default is preselected when uploading; apps serve d3's latest unless pinned.</p>

                ${
                    this._loading
                        ? html`
                    <div class="text-center py-3"><div class="spinner-border spinner-border-sm text-primary" role="status"></div></div>
                `
                        : html`
                    ${
                        this._sources.length > 0
                            ? html`
                        <table class="table table-sm mb-3">
                            <thead>
                                <tr>
                                    <th>Slug</th>
                                    <th>Label</th>
                                    <th>Default</th>
                                    <th>Public</th>
                                    <th>Versions</th>
                                    <th>Pins</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                ${this._sources.map(
                                    (s) => html`
                                    <tr>
                                        <td><code>${s.slug}</code></td>
                                        <td>${s.label}</td>
                                        <td>
                                            <input type="radio" name="default-source"
                                                ?checked="${s.isDefault}"
                                                ?disabled="${this._saving}"
                                                @change="${() => this._setDefault(s.slug)}">
                                        </td>
                                        <td>
                                            <input type="checkbox"
                                                ?checked="${s.isPublic}"
                                                ?disabled="${this._saving}"
                                                @change="${() => this._togglePublic(s)}">
                                        </td>
                                        <td>${s.versions ?? 0}</td>
                                        <td title="${(s.pins || []).map((p) => `${p.app} on ${p.asset_type}/${p.asset_slug}`).join(', ')}">${(s.pins || []).length}</td>
                                        <td class="text-end">
                                            <button class="btn btn-link btn-sm text-danger p-0" ?disabled="${this._saving || s.slug === 'd3' || (s.pins || []).length > 0}"
                                                title="${s.slug === 'd3' ? 'Every app serves d3 by default' : (s.pins || []).length > 0 ? 'Apps are pinned to this source' : 'Remove from the list; its versions stay'}"
                                                @click="${() => this._removeSource(s.slug)}">
                                                <i class="bi bi-trash"></i>
                                            </button>
                                        </td>
                                    </tr>
                                `,
                                )}
                            </tbody>
                        </table>
                    `
                            : html`<p class="text-muted small">No sources defined yet.</p>`
                    }

                    <form class="d-flex gap-2 align-items-end" @submit="${this._addSource}">
                        <div>
                            <label class="form-label small">Slug</label>
                            <input type="text" class="form-control form-control-sm" placeholder="e.g. d3" required
                                style="width: 120px;"
                                .value="${this._newSlug}"
                                @input="${(e) => (this._newSlug = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}">
                        </div>
                        <div>
                            <label class="form-label small">Label</label>
                            <input type="text" class="form-control form-control-sm" placeholder="e.g. Dice Driven Design LLC" required
                                style="width: 250px;"
                                .value="${this._newLabel}"
                                @input="${(e) => (this._newLabel = e.target.value)}">
                        </div>
                        <button type="submit" class="btn btn-primary btn-sm" ?disabled="${this._saving || !this._newSlug || !this._newLabel}">
                            Add
                        </button>
                    </form>
                `
                }
            </div>

            <div class="section">
                <h6>Landing Page Showcase</h6>
                <p class="text-muted small">Choose assets to feature on the public landing page. Order matters.</p>

                ${
                    this._showcaseAssets.length > 0
                        ? html`
                    <table class="table table-sm mb-3">
                        <thead>
                            <tr><th></th><th>Slug</th><th>Type</th><th></th></tr>
                        </thead>
                        <tbody>
                            ${this._showcaseAssets.map(
                                (a, i) => html`
                                <tr>
                                    <td>
                                        <button class="btn btn-link btn-sm p-0" ?disabled="${i === 0}" @click="${() => this._moveShowcase(i, -1)}"><i class="bi bi-arrow-up"></i></button>
                                        <button class="btn btn-link btn-sm p-0" ?disabled="${i === this._showcaseAssets.length - 1}" @click="${() => this._moveShowcase(i, 1)}"><i class="bi bi-arrow-down"></i></button>
                                    </td>
                                    <td><code>${a.slug}</code></td>
                                    <td><span class="badge bg-secondary">${a.asset_type}</span></td>
                                    <td class="text-end">
                                        <button class="btn btn-link btn-sm text-danger p-0" @click="${() => this._removeFromShowcase(a.id)}"><i class="bi bi-trash"></i></button>
                                    </td>
                                </tr>
                            `,
                            )}
                        </tbody>
                    </table>
                `
                        : html`<p class="text-muted small">No showcase assets selected.</p>`
                }

                <div style="position: relative;">
                    <input type="text" class="form-control form-control-sm" placeholder="Search assets to add..."
                        style="width: 300px;"
                        .value="${this._showcaseSearch}"
                        @input="${(e) => (this._showcaseSearch = e.target.value)}">
                    ${
                        this._filteredAddableAssets.length > 0
                            ? html`
                        <div style="position: absolute; z-index: 10; background: white; border: 1px solid #ddd; border-radius: 4px; width: 300px; max-height: 200px; overflow-y: auto;">
                            ${this._filteredAddableAssets.map(
                                (a) => html`
                                <div style="padding: 0.5rem; cursor: pointer; font-size: 0.85rem;" class="d-flex justify-content-between align-items-center"
                                    @click="${() => this._addToShowcase(a)}">
                                    <span><code>${a.slug}</code> <span class="badge bg-secondary">${a.asset_type}</span></span>
                                    <i class="bi bi-plus-circle text-primary"></i>
                                </div>
                            `,
                            )}
                        </div>
                    `
                            : nothing
                    }
                </div>
            </div>

            <div class="section">
                <h6>Discord Notifications</h6>
                <p class="text-muted small">Post commission activity to a Discord channel via webhook.</p>
                <div class="d-flex gap-2 align-items-end">
                    <div style="flex: 1;">
                        <label class="form-label small">Webhook URL</label>
                        <input type="url" class="form-control form-control-sm" placeholder="https://discord.com/api/webhooks/..."
                            .value="${this._webhookUrl}"
                            @input="${(e) => (this._webhookUrl = e.target.value)}">
                    </div>
                    <button class="btn btn-primary btn-sm" ?disabled="${this._webhookSaving}" @click="${this._saveWebhook}">
                        ${this._webhookSaving ? 'Saving...' : 'Save'}
                    </button>
                </div>
            </div>
        `;
    }
}

customElements.define('staff-settings-view', StaffSettingsView);
